import { ReactNode, useEffect, useState } from "react";
import {
  ImageUploadSkeleton,
  LeftContainer,
  ListItemImageContainer,
  MediaImagesListItemWrapper,
} from "../styles/styles";
import { UploadImageButton } from "./UploadImageButton";
import { Media } from "../types/media";
import { DeleteImageButton } from "./DeleteImageButton";
import { Image, ImageUploadResponse } from "../types/images";
import { MediaImageItem } from "./MediaImageItem";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useTranslation } from "@coworker/locales";
import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import { setMainMediaImage } from "../services/images.service";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../hooks/queryKeys";

interface MediaImagesListItemProps {
  icon?: ReactNode;
  media: Media;
  images: Image[];
  onAddStart: (fileList: FileList) => void;
  onAddEnd: (images: Image[]) => void;
  onRemove: (images: Image[]) => void;
  onUpdate: (image: Image) => void;
  isUploadingImages: boolean;
}

export const MediaImagesListItem = ({
  icon,
  media,
  images,
  onAddStart,
  onAddEnd,
  onRemove,
  onUpdate,
  isUploadingImages,
}: MediaImagesListItemProps) => {
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [mainImage, setMainImage] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    if (images && images.length > 0) {
      const mainImageFromImages =
        images.find((image) => image.orderIndex === 0)?.id || "";
      setMainImage(mainImageFromImages);
    }
  }, [images]);

  const onImageUploadStart = (fileList: FileList) => {
    setUploadingCount(fileList.length);
    onAddStart(fileList);
  };

  const onImageUploadEnd = async (fileList: ImageUploadResponse[]) => {
    onAddEnd(
      fileList.map((file) => ({
        mediaId: media.id,
        fileName: file.file_name,
        folder: file.folder,
        url: file.signed_url,
        rotation: 0,
        orderIndex: null,
      }))
    );
    setUploadingCount(0);
  };

  const handleDeleteImage = async (imageIds: string[]) => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      await onRemove(
        imageIds.map((id) => images.find((image) => image.id === id)!)
      );
      setSelectedImages([]);
    } catch (error) {
      console.error("Failed to delete images:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetMainImage = async () => {
    if (selectedImages.length > 0 && selectedImages[0]) {
      try {
        await setMainMediaImage(media.id, selectedImages[0]);
        setMainImage(selectedImages[0]);
        setSelectedImages([]);
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ImagesByMedia],
        });
      } catch (error) {
        console.error("Failed to set main image:", error);
      }
    }
  };

  if (!images) return <></>;

  return (
    <MediaImagesListItemWrapper>
      <UploadImageButton
        onImageUploadStart={onImageUploadStart}
        onImageUploadEnd={onImageUploadEnd}
        isUploadingImages={isUploadingImages}
      />
      <LeftContainer>
        <>{icon}</>
      </LeftContainer>
      <ListItemImageContainer>
        {images.map((image, index) => (
          <MediaImageItem
            key={image.id}
            image={image}
            index={index}
            selectImage={selectedImages}
            selectButtonClick={false}
            setSelectImage={setSelectedImages}
            onUpdate={onUpdate}
            mainImage={image.id === mainImage}
          />
        ))}
        {Array.from({ length: uploadingCount }).map((_, index) => (
          <ImageUploadSkeleton
            key={`skeleton-${index}`}
            height="73px"
            width="73px"
          />
        ))}
      </ListItemImageContainer>
      {selectedImages.length > 0 && (
        <>
          {selectedImages.length === 1 && (
            <FixaButton
              text={t("setAsMainImageString")}
              iconPosition="trailing"
              size="small"
              type="emphasised"
              ssrIcon={ImageIconPath}
              onClick={handleSetMainImage}
            />
          )}
          <DeleteImageButton
            deleteImage={handleDeleteImage}
            selectedImages={selectedImages}
            disabled={isDeleting}
          />
        </>
      )}
    </MediaImagesListItemWrapper>
  );
};
